/**
 * Session-expiry redirect helpers.
 *
 * Two public redirect entry points exist, and the asymmetry between them is the
 * load-bearing design decision in this file:
 *
 * - `redirectIfSessionExpired` — gated on `wasAuthenticated()`. Used by the API
 *   client layer (e.g. `AsyncRequest`, `RequestCache`), where requests can fire
 *   before login completes. We must not redirect a user who has not yet
 *   authenticated in this tab.
 *
 * - `redirectToLoginIfAuthError` — ungated. Used by `displayError` and similar
 *   snackbar paths. By the time anything is asking for a snackbar, the user has
 *   already passed through the login flow, so a 401 there always means the
 *   session has expired.
 *
 * `markAuthenticated()` is set on the first successful authenticated response
 * and cleared on sign-out (and on redirect). The flag lives in `sessionStorage`
 * for per-tab semantics.
 */

import { getQueryParam, hasQueryParam } from './LocationUtils'
import { redirectToServlet, SERVLET_COMMAND, ServletCmds } from '../constants/ServletCommands'
import { getAppV2APIBasePath } from '../constants/ApiConstants'
import { StatusCodes } from '../constants/StatusCodes'
import { RESTAPIError } from '../common/API/REST/RESTAPIError'

const SECURITY_ID: string = 'SecurityID'
const AUTH_ROUTE: string = 'auth'
const AUTH_ROUTE_SEGMENT: string = `/${AUTH_ROUTE}`
const V2_APP_PATH: string = '/CCS/App/v2/'
const AUTH_FLAG_KEY: string = 'hasAuthenticated'

const isV2App = (): boolean => window.location.pathname.startsWith(V2_APP_PATH)

const isOnAuthPage = (): boolean =>
    window.location.pathname.endsWith(AUTH_ROUTE_SEGMENT) ||
    getQueryParam(SERVLET_COMMAND) === ServletCmds.AUTHENTICATION

/**
 * Record that the current tab has seen an authenticated response.
 *
 * Stored in `sessionStorage` so it is scoped to this tab and survives
 * navigations within the SPA. Read by `redirectIfSessionExpired` to decide
 * whether a 401 means an expired session (redirect) or a request that fired
 * before login completed (ignore).
 */
export const markAuthenticated = (): void => {
    sessionStorage.setItem(AUTH_FLAG_KEY, '1')
}

/**
 * Clear the authenticated-tab flag. Called on sign-out and immediately before
 * redirecting to the login page.
 */
export const clearAuthenticated = (): void => {
    sessionStorage.removeItem(AUTH_FLAG_KEY)
}

const wasAuthenticated = (): boolean => sessionStorage.getItem(AUTH_FLAG_KEY) === '1'

let redirecting: boolean = false

/**
 * Test-only. Resets the `redirecting` re-entry guard so tests that fire a
 * redirect can be re-run within the same module instance. Not part of the
 * runtime API and never called in production code.
 *
 * @internal
 */
export const resetRedirectingForTests = (): void => {
    redirecting = false
}

const performRedirectToLogin = (): void => {
    // Concurrent 401s can race here. Guard so we only run the redirect logic
    // once per page lifetime — repeated `window.location.href` assignment is
    // idempotent, but the preceding steps (clearAuthenticated, URL building)
    // should not re-run.
    if (redirecting) return
    redirecting = true

    clearAuthenticated()
    if (isV2App()) {
        window.location.href = getAppV2APIBasePath().appendPathParams(AUTH_ROUTE).constructURL()
    } else {
        redirectToServlet(ServletCmds.AUTHENTICATION)
    }
}

const canRedirectToLogin = (error: unknown): boolean => {
    if (!(error instanceof RESTAPIError)) return false
    if (error.HTTPStatus !== StatusCodes.UNAUTHORISED) return false
    if (hasQueryParam(SECURITY_ID)) return false
    if (isOnAuthPage()) return false
    return true
}

// Servlet command for the BroadWorks SSO landing page (see GenericReactPageInit).
const BROADWORKS_SSO_COMMAND: string = 'CMD_BW_SSO'
const EXTERNAL_SSO_COMMAND: string = 'CMD_SSO_LOGIN'
const AUTH_FAILURE_STATUSES: number[] = [StatusCodes.UNAUTHORISED, StatusCodes.FORBIDDEN]

const isOnSSOLandingPage = (): boolean =>
    getQueryParam(SERVLET_COMMAND) === BROADWORKS_SSO_COMMAND || getQueryParam(SERVLET_COMMAND) === EXTERNAL_SSO_COMMAND

/**
 * True when `error` is an authentication failure (401/403) raised while on an
 * SSO landing page.
 *
 * During SSO login the user lands on this before a session
 * exists, and background calls (current user, feature info, user settings, ...)
 * come back 401 "NotLoggedIn". Those failures are not part of the SSO process:
 * they shouldn't flash an error toast for the ~1s before the page redirects
 * post-login, nor should a stray 401 bounce the user to the login page
 * mid-flow. `displayError` checks this ahead of its redirect/snackbar logic.
 *
 * Genuine SSO-process errors (invalid token — shown inline on the page; sign-up
 * failures — not auth-failures) are unaffected. (APD-12918)
 *
 * @param error - The error to inspect. Both error subtypes raised by the API
 *                clients (`RESTAPIError`, `InternalAPIError`) extend `Error`.
 * @returns - `true` if the error is an expected pre-auth auth-failure on the
 *            SSO page and should be silently ignored.
 */
export const isIgnorableSSOError = (error: Error): boolean => {
    if (!isOnSSOLandingPage()) return false

    // RESTAPIError / InternalAPIError both carry a numeric HTTPStatus at runtime,
    // though it isn't part of the base Error type.
    const status: Definable<number> = (error as { HTTPStatus?: number }).HTTPStatus

    return typeof status === 'number' && AUTH_FAILURE_STATUSES.includes(status)
}

/**
 * Redirect to the login page if `error` is a 401 from a context where the user
 * is expected to already be authenticated. Used by `displayError` and similar
 * snackbar paths.
 *
 * Unlike `redirectIfSessionExpired`, this does **not** check the
 * `wasAuthenticated` flag — callers of this function only run post-login.
 *
 * @param error - The error to inspect. Must be a `RESTAPIError` carrying a 401
 *                for a redirect to be issued; anything else is ignored.
 * @returns - `true` if a redirect was issued (the page is about to unload);
 *            `false` if the error should be handled normally.
 */
export const redirectToLoginIfAuthError = (error: unknown): boolean => {
    if (!canRedirectToLogin(error)) return false
    performRedirectToLogin()
    return true
}

/**
 * Redirect to the login page if `error` looks like an expired session, but
 * only if the tab has previously seen an authenticated response. Used by the
 * API client layer (`AsyncRequest`, `RequestCache`), where requests can fire
 * before login completes — we don't want to bounce a user who has never been
 * logged in to a "your session expired" page.
 *
 * @param error - The error to inspect. Must be a `RESTAPIError` carrying a 401
 *                for a redirect to be issued; anything else is ignored.
 * @returns - `true` if a redirect was issued (the page is about to unload);
 *            `false` if the error should be handled normally.
 */
export const redirectIfSessionExpired = (error: unknown): boolean => {
    if (!wasAuthenticated()) return false
    if (!canRedirectToLogin(error)) return false
    performRedirectToLogin()
    return true
}
